const BASIS_POINTS_TO_PERCENTAGE = 10000; // 10000 basis points = 100%
const ORE_TO_NOK = 100; // 100 øre = 1 NOK
const MONTHS_IN_YEAR = 12;

export type CashbackEstimate = {
  readonly monthly: number;
  readonly yearly: number;
};

/**
 * Calculate cashback estimate for partner
 *
 * Calculates partner cashback based on pricing.
 * Mirrors backend calculation logic from cashback.utils.ts.
 *
 * @param pricePerSeatNOK - Price per seat in NOK (not øre)
 * @param availableSeats - Number of available seats/licenses
 * @param billingPeriod - Billing period ("monthly" | "yearly")
 * @param cashbackPercentage - Cashback percentage in basis points (2000 = 20%)
 * @returns Cashback estimate or null if any required parameter is missing
 *
 * @example
 * calculateCashbackEstimate(299, 230, "monthly", 2000)
 * // Returns: { monthly: 13754, yearly: 165048 }
 *
 * calculateCashbackEstimate(null, 230, "monthly", 2000)
 * // Returns: null
 */
export const calculateCashbackEstimate = (
  pricePerSeatNOK: number | null,
  availableSeats: number | null,
  billingPeriod: string | null,
  cashbackPercentage: number | null,
): CashbackEstimate | null => {
  // Guard: All parameters required
  if (
    pricePerSeatNOK === null ||
    availableSeats === null ||
    billingPeriod === null ||
    cashbackPercentage === null
  ) {
    return null;
  }

  // Guard: Must have positive values
  if (pricePerSeatNOK <= 0 || availableSeats <= 0 || cashbackPercentage < 0) {
    return null;
  }

  // Convert NOK to øre for accurate calculation
  const pricePerSeatOre = pricePerSeatNOK * ORE_TO_NOK;

  // Calculate monthly revenue in øre
  const monthlyRevenueOre = pricePerSeatOre * availableSeats;

  // Calculate cashback in øre (basis points to percentage: divide by 10000)
  const monthlyCashbackOre = Math.round(
    (monthlyRevenueOre * cashbackPercentage) / BASIS_POINTS_TO_PERCENTAGE
  );

  // Calculate yearly cashback
  const yearlyCashbackOre = monthlyCashbackOre * MONTHS_IN_YEAR;

  // Convert back to NOK and return
  return {
    monthly: Math.round(monthlyCashbackOre / ORE_TO_NOK),
    yearly: Math.round(yearlyCashbackOre / ORE_TO_NOK),
  };
};

/**
 * Calculate monthly revenue
 *
 * Calculates monthly revenue based on pricing.
 *
 * @param pricePerSeatNOK - Price per seat in NOK
 * @param availableSeats - Number of available seats
 * @returns Monthly revenue in NOK or null if parameters missing
 *
 * @example
 * calculateMonthlyRevenue(299, 230) // 68770
 * calculateMonthlyRevenue(null, 230) // null
 */
export const calculateMonthlyRevenue = (
  pricePerSeatNOK: number | null,
  availableSeats: number | null,
): number | null => {
  if (pricePerSeatNOK === null || availableSeats === null) {
    return null;
  }

  if (pricePerSeatNOK <= 0 || availableSeats <= 0) {
    return null;
  }

  return pricePerSeatNOK * availableSeats;
};

/**
 * Calculate yearly revenue
 *
 * Calculates yearly revenue based on pricing.
 *
 * @param pricePerSeatNOK - Price per seat in NOK
 * @param availableSeats - Number of available seats
 * @returns Yearly revenue in NOK or null if parameters missing
 *
 * @example
 * calculateYearlyRevenue(299, 230) // 825240
 */
export const calculateYearlyRevenue = (
  pricePerSeatNOK: number | null,
  availableSeats: number | null,
): number | null => {
  const monthlyRevenue = calculateMonthlyRevenue(pricePerSeatNOK, availableSeats);

  if (monthlyRevenue === null) {
    return null;
  }

  return monthlyRevenue * MONTHS_IN_YEAR;
};
